import { useCallback, useLayoutEffect, useRef, useState } from "react";

const VIEWPORT_EDGE_MARGIN = 8;
const MENU_GAP_PX = 4;
/** When the menu is taller than both above/below wedges, keep at least this much scroll area. */
const MIN_MENU_SCROLL_HEIGHT_PX = 120;

function assignForwardRef(forwardedRef, node) {
  if (typeof forwardedRef === "function") {
    forwardedRef(node);
  } else if (forwardedRef != null) {
    // eslint-disable-next-line no-param-reassign -- forwardRef/.current merge
    forwardedRef.current = node;
  }
}

function computeMenuPosition(labelledBy, menuNode, menuAlign) {
  const toggle = document.getElementById(labelledBy);
  if (!toggle || !menuNode) {
    return { top: 0, left: 0, maxHeight: null };
  }

  const rect = toggle.getBoundingClientRect();
  const menuHeight = menuNode.offsetHeight;
  const viewportH = globalThis.window.innerHeight;

  let top = rect.bottom + MENU_GAP_PX;
  let maxHeight = null;

  const bottomEdge = top + menuHeight;
  if (bottomEdge > viewportH - VIEWPORT_EDGE_MARGIN) {
    const topIfFlipped = rect.top - MENU_GAP_PX - menuHeight;
    if (topIfFlipped >= VIEWPORT_EDGE_MARGIN) {
      top = topIfFlipped;
    } else {
      const availableBelow = viewportH - VIEWPORT_EDGE_MARGIN - (rect.bottom + MENU_GAP_PX);
      const availableAbove = rect.top - MENU_GAP_PX - VIEWPORT_EDGE_MARGIN;

      if (availableBelow >= availableAbove) {
        top = rect.bottom + MENU_GAP_PX;
        maxHeight = Math.max(availableBelow, MIN_MENU_SCROLL_HEIGHT_PX);
      } else {
        top = VIEWPORT_EDGE_MARGIN;
        maxHeight = Math.max(availableAbove, MIN_MENU_SCROLL_HEIGHT_PX);
      }
    }
  }

  return { top, left: menuAlign === "start" ? rect.left : rect.right, maxHeight };
}

/**
 * Fixed positioning for the portaled Bootstrap dropdown menu: prefer below the toggle,
 * flip above or clamp height when the viewport would clip items. The menu's right edge lines up with
 * the toggle's ("end", the default), or its left edge does ("start").
 */
export default function usePortalDropdownMenuPosition({
  labelledBy,
  show,
  children,
  forwardedRef,
  menuWidth,
  menuAlign = "end",
}) {
  const menuRef = useRef(null);
  const attachMenuRef = useCallback(
    (node) => {
      menuRef.current = node;
      assignForwardRef(forwardedRef, node);
    },
    [forwardedRef],
  );

  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: null });

  useLayoutEffect(() => {
    if (!show || !labelledBy) {
      return undefined;
    }

    const updatePosition = () => {
      setPosition(computeMenuPosition(labelledBy, menuRef.current, menuAlign));
    };

    updatePosition();
    const rafId = globalThis.requestAnimationFrame(updatePosition);
    globalThis.window.addEventListener("resize", updatePosition);
    return () => {
      globalThis.cancelAnimationFrame(rafId);
      globalThis.window.removeEventListener("resize", updatePosition);
    };
  }, [show, labelledBy, children, menuAlign]);

  const menuStyle = {
    position: "fixed",
    top: position.top,
    left: position.left,
    // Right-align to toggle: shift left by the menu's own width
    ...(menuAlign === "start" ? {} : { transform: "translateX(-100%)" }),
    zIndex: 1060,
    minWidth: menuWidth,
    ...(position.maxHeight != null ? { maxHeight: position.maxHeight, overflowY: "auto" } : {}),
  };

  return { attachMenuRef, menuStyle };
}
